import { Modal } from "@mui/material";
import type { TImportTitles } from "components/ImportData/import_data";
import { getBasicImportTypeData, getImportKeys, getSingleValueCsvHeaders } from "components/ImportData/import_data";
import type { TImportDataDictionary } from "components/ImportData/type";
import { getElementCustomFields } from "components/ImportData/utils";
import MultiStep from "components/shared/MultiStep";
import type { IBeneficiar } from "containers/Beneficiars/types";
import type { ITitle } from "containers/Titles/types";
import { PortalType } from "contexts/portal.context";
import { isObjectEmpty } from "helpers/utils";
import { useCompanySettings } from "hooks/useCompanySettings";
import { t } from "i18next";
import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useNavigation } from "react-router-dom";
import { fetchBeneficiar } from "store/beneficiaryStore";
import { getCalculationBaseColumnLabels } from "store/calculationBaseColumnLabelsStore";
import type { ITerm } from "store/managementStore";
import { useAppDispatch, useAppSelector } from "store/store";
import { fetchTitle } from "store/titleStore";

import ConfirmationStep from "./ConfirmationStep";
import MappingStep from "./MappingStep";
import PreparationStep from "./PreparationStep";
import UpdateTypeStep from "./UpdateTypeStep";
import UploadStep from "./UploadStep";
import VerificationStep from "./VerificationStep";

export type TUpdateElement = "beneficiars" | "terms" | "titles";

interface IBulkUpdatesContainer {
    from: "beneficiars" | "titles";
    openModal: boolean;
    closeModal: () => void;
}

export interface IHandleStep {
    change: (_: any, idx: number) => void;
    next: () => void;
    previous: () => void;
}

export type TFileData = Record<string, any>[];
export type TExampleData = IBeneficiar | ITitle | ITerm;

export const getImportTypeTranslation = (element: TImportDataDictionary) => {
    switch (element) {
        case "titles":
            return t("Titles.Titles").toLowerCase();
        case "terms":
            return t("Terms.Terms").toLowerCase();
        default:
            return t("Beneficiars.Beneficiars").toLowerCase();
    }
};

/**
 * Container that handles bulk updates.
 */
const BulkUpdatesContainer = ({ openModal, closeModal, from }: IBulkUpdatesContainer) => {
    const portalType = useContext(PortalType);
    const [t] = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const { customFields } = useAppSelector((state) => state.customFields);
    const { labels: calculationBaseColumnLabels, labelsStatus: calculationBaseColumnLabelsStatus } = useAppSelector(
        (state) => state.calculationBaseColumnLabels,
    );

    const { getCompanySettings, companyCountryCode, countryFeatures, internationalBankingEnabled } =
        useCompanySettings();
    const isUploadAsync = getCompanySettings("uses_worker_mode");
    const usesVAT = getCompanySettings("uses_price_with_vat");
    const usesMultipleBrands = getCompanySettings("uses_multiple_brands");
    const usesTitleCompensation = getCompanySettings("uses_intertitle_compensation");
    const usesMultipleCurrencies = getCompanySettings("uses_multiple_currencies");
    const usesAdvanceCompensation = getCompanySettings("uses_interterm_compensation");
    const usesInitialAdvance = getCompanySettings("uses_initial_advance");
    const usesCumulativeRoyalties = getCompanySettings("uses_cumulative_royalties");
    const allowsSubscriptionChannels = getCompanySettings("allows_subscription_channels");

    const askForElement = ["beneficiars", "titles"].includes(from);
    const [element, setElement] = useState<TImportDataDictionary | undefined>(askForElement ? undefined : from);
    const [activeStep, setActiveStep] = useState(0);
    const [loadedData, setLoadedData] = useState<TFileData>();
    const [fileTitles, setFileTitles] = useState<string[]>([]);
    const [fileName, setFileName] = useState("");
    const [selectedValues, setSelectedValues] = useState<Record<string, string>>({});
    const [exampleData, setExampleData] = useState<TExampleData>();

    useEffect(() => {
        if (element === "titles" && calculationBaseColumnLabelsStatus === "idle") {
            dispatch(getCalculationBaseColumnLabels());
        }
    }, [element, calculationBaseColumnLabelsStatus, dispatch]);

    const currentCustomFields = useMemo(() => {
        if (!element) return;
        return getElementCustomFields(element, customFields);
    }, [element]);

    const customTurnoverLabels = useMemo(() => {
        const labels: { quantity?: string; turnover?: string; turnover_1?: string; turnover_2?: string } = {};
        for (const row of calculationBaseColumnLabels) {
            labels[row.calculationBaseColumn] = row.label;
        }
        return labels;
    }, [calculationBaseColumnLabels]);

    const allConditions = useMemo(
        () => ({
            customFields: currentCustomFields,
            usesMultipleBrands,
            usesVAT,
            usesTitleCompensation,
            usesMultipleCurrencies,
            usesDirectPrecount:
                countryFeatures?.precountMode === "direct" && (countryFeatures?.precount?.options?.length ?? 0) > 0,
            usesAdvanceCompensation,
            usesInitialAdvance,
            usesCumulativeRoyalties,
            allowsSubscriptionChannels,
            bankingFields: countryFeatures?.bankingFields,
            hasVat: (countryFeatures?.vat?.options?.length ?? 0) > 0,
            urssaf: countryFeatures?.urssaf,
            internationalBankingEnabled: !!internationalBankingEnabled,
            customTurnoverLabels,
            isBulkUpdate: true,
        }),
        [
            currentCustomFields,
            usesMultipleBrands,
            usesVAT,
            usesTitleCompensation,
            usesMultipleCurrencies,
            countryFeatures?.precountMode,
            countryFeatures?.precount?.options?.length,
            usesAdvanceCompensation,
            usesInitialAdvance,
            usesCumulativeRoyalties,
            allowsSubscriptionChannels,
            countryFeatures?.bankingFields,
            countryFeatures?.vat?.options?.length,
            countryFeatures?.urssaf,
            internationalBankingEnabled,
            customTurnoverLabels,
        ],
    );

    const crealoKeys: TImportTitles = useMemo(() => {
        if (!element) return;
        return getImportKeys(element, allConditions, "label", portalType);
    }, [element, allConditions, portalType]);

    const acceptedKeys = useMemo(() => {
        if (!element) return;
        return getImportKeys(element, allConditions, "acceptedKeys", portalType);
    }, [element, allConditions, portalType]);

    /**
     * Keys whose value accepts float numbers.
     */
    const floatKeys: string[] = useMemo(() => {
        if (!element) return [];
        const elementKeys = getBasicImportTypeData(element, "book", allConditions).keys;
        const keys = Object.keys(elementKeys).filter((key) => elementKeys[key].floatKey);
        if (customTurnoverLabels.turnover_1) keys.push("turnover_1");
        if (customTurnoverLabels.turnover_2) keys.push("turnover_2");
        return keys;
    }, [element, allConditions, customTurnoverLabels]);

    const singleValueHeaders = useMemo(() => {
        if (!element) return undefined;
        return getSingleValueCsvHeaders(element, allConditions, portalType);
    }, [element, allConditions, portalType]);

    /**
     * Keys whose value accepts repeated values.
     */
    const repeatedKeys: string[] = useMemo(() => {
        if (!element) return [];
        const elementKeys = getBasicImportTypeData(element, "book", allConditions).keys;
        const keys = Object.keys(elementKeys).filter((key) => elementKeys[key].repeatedKey);
        if (customTurnoverLabels.turnover_1) keys.push("turnover_1");
        if (customTurnoverLabels.turnover_2) keys.push("turnover_2");
        return keys;
    }, [element, allConditions, customTurnoverLabels]);

    /**
     * Adds a _ in front of all our keys to remove all possibility they have
     * a similar key in their file.
     */
    const formattedCrealoKeys: TImportTitles | undefined = useMemo(() => {
        if (!crealoKeys) return;
        const keys = {};
        Object.keys(crealoKeys).forEach((key) => {
            keys[`_${key}`] = crealoKeys[key];
        });

        return { ["_id"]: t("BulkUpdates.ID"), ...keys };
    }, [crealoKeys]);

    const handleCloseModal = () => {
        setActiveStep(0);
        setLoadedData(undefined);
        setFileTitles([]);
        setSelectedValues({});
        setExampleData(undefined);
        setElement(undefined);
        if (element && ["beneficiars", "titles"].includes(element)) {
            if (exampleData) {
                element === "titles" ? dispatch(fetchTitle(exampleData.id)) : dispatch(fetchBeneficiar(exampleData.id));
            }
        } else {
            if (exampleData) {
                navigate(
                    `/${from}/${
                        from === "titles" ? (exampleData as ITerm).titleId : (exampleData as ITerm).beneficiarId
                    }`,
                );
            }
        }
        closeModal();
    };

    /**
     * Options when many update types can be done from same space.
     */
    const elementOptions: Record<string, TImportDataDictionary[]> = {
        beneficiars: ["beneficiars", "terms"],
        titles: ["titles", "terms"],
    };

    // Makes sure data is clean when opening modal.
    useEffect(() => {
        setActiveStep(0);
        setLoadedData(undefined);
        setFileTitles([]);
        setSelectedValues({});
        setExampleData(undefined);
        setElement(undefined);
    }, []);

    return (
        <Modal open={openModal} onClose={handleCloseModal}>
            <MultiStep
                title={
                    !element
                        ? t("BulkUpdates.BulkUpdate")
                        : t("BulkUpdates.ModalTitle", { element: getImportTypeTranslation(element) })
                }
                activeStep={activeStep}
                onClose={handleCloseModal}
                animatedStepper
                style={{ height: "85%", minWidth: "1000px" }}
                steps={[
                    {
                        title: t("BulkUpdates.Steps.Choice"),
                        hidden: !["titles", "beneficiars"].includes(from),
                        component: (
                            <UpdateTypeStep
                                activeStep={activeStep}
                                setActiveStep={setActiveStep}
                                setElement={setElement}
                                options={elementOptions[from]}
                                element={element}
                            />
                        ),
                    },
                    {
                        title: t("BulkUpdates.Steps.Preparation"),
                        component: (
                            <PreparationStep element={element} activeStep={activeStep} setActiveStep={setActiveStep} />
                        ),
                    },
                    {
                        title: t("BulkUpdates.Steps.Uploading"),
                        component: (
                            <UploadStep
                                setFileTitles={setFileTitles}
                                setLoadedData={setLoadedData}
                                activeStep={activeStep}
                                setActiveStep={setActiveStep}
                                setSelectedValues={setSelectedValues}
                                setFileName={setFileName}
                                floatKeys={floatKeys}
                                singleValueHeaders={singleValueHeaders}
                                setExampleData={setExampleData}
                            />
                        ),
                    },
                    {
                        title: t("BulkUpdates.Steps.Mapping"),
                        component: (
                            <MappingStep
                                loadedData={loadedData}
                                setLoadedData={setLoadedData}
                                fileTitles={fileTitles}
                                activeStep={activeStep}
                                setActiveStep={setActiveStep}
                                crealoKeys={formattedCrealoKeys}
                                selectedValues={selectedValues}
                                setSelectedValues={setSelectedValues}
                                exampleData={exampleData}
                                setExampleData={setExampleData}
                                element={element}
                                repeatedKeys={repeatedKeys}
                                acceptedKeys={acceptedKeys}
                            />
                        ),
                    },
                    {
                        title: t("BulkUpdates.Steps.Verification"),
                        component: (
                            <VerificationStep
                                activeStep={activeStep}
                                setActiveStep={setActiveStep}
                                loadedData={loadedData}
                                crealoKeys={formattedCrealoKeys}
                                exampleData={exampleData}
                                selectedValues={selectedValues}
                                floatKeys={floatKeys}
                                element={element}
                            />
                        ),
                    },
                    {
                        title: t("BulkUpdates.Steps.Confirming"),
                        component: (
                            <ConfirmationStep
                                activeStep={activeStep}
                                setActiveStep={setActiveStep}
                                loadedData={loadedData}
                                setLoadedData={setLoadedData}
                                fileTitles={fileTitles}
                                selectedValues={selectedValues}
                                filename={fileName}
                                element={element}
                                companySettings={{
                                    isUploadAsync,
                                    usesVAT,
                                    companyCountryCode,
                                    internationalBankingEnabled,
                                }}
                                bankingFields={countryFeatures.bankingFields}
                                floatKeys={floatKeys}
                                closeModal={handleCloseModal}
                                currentCustomFields={currentCustomFields}
                            />
                        ),
                    },
                ]}
            />
        </Modal>
    );
};

export default BulkUpdatesContainer;
